import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import AlertBox from 'components/ui/AlertBox';
import { ActionButton, LinkButton } from 'components/ui/Button';
import { useManagedMutation } from 'utils/managedHooks';
import { ADD_ADDITIONAL_DETAILS, UPDATE_ADDITIONAL_DETAILS } from 'store/mutations';
import { OPERATIONAL_REMARK_ADDITIONAL_DETAILS_KEY } from 'constants/booking';
import { Content, RemarkContent, StyledForm, StyledTextArea, ExitButton } from './RemarkDetails.styles';

const RemarkDetails = ({ bookingReference, currentRemark }) => {
  const [remark, setRemark] = useState(null);
  const [isOpen, toggleForm] = useState(false);
  const [isNoteAdded, setNoteAdded] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const handleTextChange = (event) => setRemark(event.target.value);
  const handleLinkToggle = () => toggleForm(true);
  const [addRemark, { loading }] = useManagedMutation(ADD_ADDITIONAL_DETAILS);
  const [updateRemark, { loading: updateRemarkLoading }] = useManagedMutation(UPDATE_ADDITIONAL_DETAILS);

  if (currentRemark && !remark && !isEdit) {
    setRemark(currentRemark.value);
    setNoteAdded(true);
  }

  const handleFormSubmit = async () => {
    const additionalDetails = [
      {
        key: OPERATIONAL_REMARK_ADDITIONAL_DETAILS_KEY,
        value: remark
      }
    ];
    const addUpdateRemark = !isNoteAdded ? addRemark : updateRemark;
    await addUpdateRemark({
      variables: {
        bookingReference,
        additionalDetails
      }
    });
    setNoteAdded(true);
    toggleForm(false);
  };

  const handleExit = () => {
    toggleForm(false);
  };

  const handleEdit = () => {
    setIsEdit(true);
    toggleForm(true);
  };

  const addNoteLink = (
    <LinkButton data-cy="remark-add-new-link" onClick={handleLinkToggle}>
      <FormattedMessage id="notes.remark.add" />
    </LinkButton>
  );

  const isSubmitDisabled = !remark || remark.length === 0 || remark.match(/^\s*$/);

  const shouldNotDisplayDefault = isNoteAdded && !isOpen;

  return (
    <>
      {!isNoteAdded && !isOpen && (
        <AlertBox variant={AlertBox.variants.INFO}>
          <FormattedMessage id="notes.remark.header" values={{ addNoteButton: addNoteLink }} />
        </AlertBox>
      )}
      {isOpen && (
        <StyledForm onSubmit={handleFormSubmit}>
          <div>
            <StyledTextArea
              name="note"
              value={remark}
              onChange={handleTextChange}
              required
              data-cy="remark-add-new-text"
              data-testid="add-form"
              disabled={loading || updateRemarkLoading}
            />
          </div>
          {isEdit ? (
            <ActionButton data-cy="remark-edit-submit" loading={loading || updateRemarkLoading}>
              <FormattedMessage id="notes.remark.save" />
            </ActionButton>
          ) : (
            <ActionButton data-cy="remark-add-submit" loading={loading || updateRemarkLoading} disabled={isSubmitDisabled}>
              <FormattedMessage id="notes.remark.add" />
            </ActionButton>
          )}
          <ExitButton type="button" data-cy="exit-passengers" onClick={handleExit}>
            <FormattedMessage id="buttons.exit" />
          </ExitButton>
        </StyledForm>
      )}
      {shouldNotDisplayDefault && (
        <AlertBox variant={AlertBox.variants.INFO}>
          <Content>
            <RemarkContent>{remark}</RemarkContent>
            <LinkButton data-cy="remark-edit-link" onClick={handleEdit} padding={0}>
              <FormattedMessage id="notes.remark.edit" />
            </LinkButton>
          </Content>
        </AlertBox>
      )}
    </>
  );
};

RemarkDetails.propTypes = {
  bookingReference: PropTypes.string.isRequired,
  currentRemark: PropTypes.string
};

export default RemarkDetails;
